import { useState } from 'react';
import { KNOWN_DISTRICTS } from '../lib/types';
import type { HeatmapResult } from '../lib/types';
import { useApiQuery } from '../lib/useApi';
import { ErrorNote, PageHeader, Spinner } from '../ui/components';

const METRICS = [
  { value: 'cases' as const, label: 'Cases' },
  { value: 'usage' as const, label: 'Service usage' },
];

export function Heatmap() {
  const [metric, setMetric] = useState<'cases' | 'usage'>('cases');
  const [month, setMonth] = useState('');

  const qs = new URLSearchParams({ metric, ...(month ? { month } : {}) }).toString();
  const heatmapQ = useApiQuery<HeatmapResult>(`/api/v1/analytics/heatmap?${qs}`);

  const byDistrict = new Map((heatmapQ.data?.items ?? []).map((i) => [i.district, i.count]));
  const rows = KNOWN_DISTRICTS.map((d) => ({ district: d, count: byDistrict.get(d) ?? null }));
  const maxCount = Math.max(1, ...rows.map((r) => r.count ?? 0));
  const sorted = [...rows].sort((a, b) => (b.count ?? -1) - (a.count ?? -1) || a.district.localeCompare(b.district));

  return (
    <div>
      <PageHeader
        title="District heatmap"
        subtitle="Where government activity is concentrated, by district"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex overflow-hidden rounded-lg border border-slate-300">
              {METRICS.map((m) => (
                <button
                  key={m.value}
                  onClick={() => setMetric(m.value)}
                  className={`px-3 py-1.5 text-xs font-semibold transition ${
                    metric === m.value ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <input
              type="month"
              className="field-input w-36 !py-1.5 text-xs"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              aria-label="Filter by month"
            />
          </div>
        }
      />

      {heatmapQ.error ? (
        <div className="mb-4">
          <ErrorNote message={heatmapQ.error} />
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        {heatmapQ.loading ? (
          <Spinner />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">District</th>
                <th className="table-head">{metric === 'cases' ? 'Cases opened' : 'Service uses'}</th>
                <th className="table-head w-56">Relative volume</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) =>
                r.count === null ? (
                  <tr key={r.district}>
                    <td className="table-cell font-medium">{r.district}</td>
                    <td className="table-cell italic text-slate-400" colSpan={2}>
                      — (too few to disclose)
                    </td>
                  </tr>
                ) : (
                  <tr key={r.district}>
                    <td className="table-cell font-medium">{r.district}</td>
                    <td className="table-cell tabular-nums">{r.count.toLocaleString()}</td>
                    <td className="table-cell">
                      <div className="h-2 w-full rounded-full bg-slate-100">
                        <div
                          className="h-2 rounded-full bg-brand-500"
                          style={{ width: `${Math.max(2, (r.count / maxCount) * 100)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400">
        {(heatmapQ.data?.suppressed ?? 0) > 0
          ? `${heatmapQ.data?.suppressed} district cell(s) were withheld by the API for having fewer than 5 underlying records.`
          : 'No district cells were withheld for this selection.'}{' '}
        Any district shown as &ldquo;— (too few to disclose)&rdquo; had fewer than 5 underlying records — possibly zero —
        protecting against re-identification of individuals in small districts; the API omits the cell entirely rather
        than revealing an exact small number, so this portal cannot and does not distinguish the two cases.
      </p>
    </div>
  );
}
